/**
 * Fleet tracking: the telematics provider connection (Routevision) and the
 * link between a provider's trackers and our own vehicles.
 *
 * The two enum tuples mirror Prisma enums in declaration order; a backend
 * spec (`fleet-tracking-enums.spec.ts`) fails at compile time and at run time
 * if one side gains a value the other lacks. Dates on the wire are ISO
 * strings.
 */
/** Mirrors the Prisma `TelematicsProvider` enum. */
export const TELEMATICS_PROVIDERS = ["ROUTEVISION"];
/**
 * Mirrors the Prisma `TelematicsLinkSource` enum. `MANUAL` on an unlinked
 * tracker means someone unlinked it on purpose, so the automatic plate match
 * leaves it alone.
 */
export const TELEMATICS_LINK_SOURCES = ["AUTO", "MANUAL"];
/**
 * Where the saved Routevision login stands.
 *
 * - `UNVERIFIED`: saved, not checked since.
 * - `OK`: the last check logged in and read the fleet.
 * - `INVALID_CREDENTIALS`: Routevision refused the login. Polling stops until
 *   the login is changed, so repeated attempts never lock the account.
 * - `FORBIDDEN`: logged in, but the Routevision user may not read vehicle
 *   locations. This is an account setting at Routevision, never "private".
 */
export const ROUTEVISION_AUTH_STATUSES = [
    "UNVERIFIED",
    "OK",
    "INVALID_CREDENTIALS",
    "FORBIDDEN",
];
/** Why the last call to Routevision failed. */
export const ROUTEVISION_ERROR_CODES = [
    "INVALID_CREDENTIALS",
    "FORBIDDEN",
    "RATE_LIMITED",
    "UNAVAILABLE",
    "UNEXPECTED_RESPONSE",
];
/**
 * What a linked vehicle is doing, derived from its latest report.
 *
 * - `OFFLINE`: no message from the tracker for more than
 *   {@link FLEET_OFFLINE_AFTER_MS}. A van whose tracker went quiet is not
 *   "stopped": nobody knows where it is now.
 * - `NO_FIX`: the tracker talks but reports no position.
 * - `MOVING`: faster than {@link FLEET_MOVING_ABOVE_KMH}.
 * - `STOPPED`: otherwise.
 */
export const FLEET_VEHICLE_STATES = [
    "MOVING",
    "STOPPED",
    "NO_FIX",
    "OFFLINE",
];
export const FLEET_OFFLINE_AFTER_MS = 10 * 60 * 1000;
export const FLEET_MOVING_ABOVE_KMH = 3;
/**
 * Whether a tracker's last message is too old (or missing, or unreadable) to
 * say where the van is now. The one place the OFFLINE threshold is applied.
 */
export function isFleetMessageStale(messageAt, now) {
    const at = messageAt === null ? Number.NaN : Date.parse(messageAt);
    return Number.isNaN(at) || now.getTime() - at > FLEET_OFFLINE_AFTER_MS;
}
/**
 * The state of a vehicle at `now`. Shared so the map can move a van to
 * OFFLINE as time passes without waiting for the next report.
 */
export function fleetVehicleState(vehicle, now) {
    if (isFleetMessageStale(vehicle.messageAt, now)) {
        return "OFFLINE";
    }
    if (vehicle.lat === null || vehicle.lng === null) {
        return "NO_FIX";
    }
    return (vehicle.speedKmh ?? 0) > FLEET_MOVING_ABOVE_KMH
        ? "MOVING"
        : "STOPPED";
}
/** Why a poll did or did not read Routevision. */
export const FLEET_POLL_STATUSES = [
    "POLLED",
    "SKIPPED",
    "BUSY",
    "FAILED",
];
// ── Delivery addresses on the map (geocoding) ─────────────────────────────
/**
 * Where a delivery address stands on the map. PENDING waits for the lookup;
 * every status but OK and PENDING needs a person to place the address. In
 * the Prisma `GeocodeStatus` declaration order.
 */
export const GEOCODE_STATUSES = [
    "PENDING",
    "OK",
    "NOT_FOUND",
    "AMBIGUOUS",
    "NO_POSTCODE",
    "NO_HOUSE_NUMBER",
    "UNSUPPORTED_COUNTRY",
];
/** The statuses a person has to resolve by placing the address by hand. */
export const GEOCODE_NEEDS_ATTENTION = [
    "NOT_FOUND",
    "AMBIGUOUS",
    "NO_POSTCODE",
    "NO_HOUSE_NUMBER",
    "UNSUPPORTED_COUNTRY",
];
/** Who placed an address, in the Prisma `GeocodeSource` order. */
export const GEOCODE_SOURCES = ["PDOK", "MANUAL"];
// ── Delivery stops and route runs ─────────────────────────────────────────
/**
 * Where a route run's van came from: the picking batches that carry the
 * orders, or the published weekly plan. In the Prisma `RouteRunAssignmentSource`
 * declaration order.
 */
export const ROUTE_RUN_ASSIGNMENT_SOURCES = ["PICKING", "PLANNING"];
/**
 * How a visit was decided: `DWELL` by the van's position alone,
 * `DWELL_ADDRESS` when the tracker's own address also named the stop, without a
 * break, for as long as the dwell time (3 minutes to start with); the visit is
 * dated from the first fix of the stay, which can come before that run.
 * In the Prisma `FleetVisitEvidence` declaration order.
 */
export const FLEET_VISIT_EVIDENCES = ["DWELL", "DWELL_ADDRESS"];
